import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash2, Loader2, Film, Upload, LogOut, User, Globe, Lock, Copy, Pencil, Check, X, FolderInput } from 'lucide-react';
import Layout from '../components/Layout';
import LoginModal from '../components/LoginModal';
import ThemeToggle from '../components/ThemeToggle';
import { getProjects, createProject, processProject, deleteProject, setProjectPublic, duplicateProject, updateUsername, importProject } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Button } from "../components/ui/button";
import { formatDuration } from '../lib/utils';

export default function Dashboard() {
    const { user, loading: authLoading, logout, checkAuth } = useAuth();
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showNewProject, setShowNewProject] = useState(false);
    const [newTitle, setNewTitle] = useState('');
    const [projectFormat, setProjectFormat] = useState('16:9');
    const [uploading, setUploading] = useState(false);
    const [sensitivity, setSensitivity] = useState('normal');
    const fileInputRef = useRef(null);
    const navigate = useNavigate();
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [editingUsername, setEditingUsername] = useState(false);
    const [usernameDraft, setUsernameDraft] = useState('');
    const [importing, setImporting] = useState(false);
    const importInputRef = useRef(null);

    const handleImportZip = async (e) => {
        const file = e.target.files?.[0];
        if (importInputRef.current) importInputRef.current.value = '';
        if (!file) return;
        setImporting(true);
        const tid = toast.loading('Import en cours…');
        try {
            const project = await importProject(file);
            setProjects((prev) => [project, ...prev]);
            toast.dismiss(tid);
            toast.success(`Projet « ${project.title} » importé`);
        } catch (err) {
            toast.dismiss(tid);
            toast.error(err?.response?.data?.detail || 'Échec de l\'import');
        } finally { setImporting(false); }
    };

    const handleSaveUsername = async () => {
        const val = usernameDraft.trim();
        if (!val) { setEditingUsername(false); return; }
        try {
            await updateUsername(val);
            await checkAuth(); // refresh user object
            toast.success('Pseudo mis à jour');
        } catch { toast.error('Échec de la mise à jour'); }
        setEditingUsername(false);
    };

    useEffect(() => { if (user) loadProjects(); }, [user]);

    const loadProjects = async () => {
        setLoading(true);
        try { setProjects(await getProjects()); }
        catch (e) { console.error(e); }
        finally { setLoading(false); }
    };

    const handleCreateProject = async (e) => {
        e.preventDefault();
        const file = fileInputRef.current?.files?.[0];
        if (!newTitle.trim()) { toast.error("Le titre du projet est requis"); return; }
        setUploading(true);
        try {
            const project = await createProject(newTitle.trim(), file || null, projectFormat);
            if (file) {
                const thresholdMap = { low: 35, normal: 20, high: 10 };
                toast.success("Analyse de la vidéo en cours...");
                processProject(project.id, thresholdMap[sensitivity] || 20);
            } else {
                toast.success("Projet créé");
            }
            setShowNewProject(false);
            setNewTitle('');
            setSensitivity('normal');
            if (fileInputRef.current) fileInputRef.current.value = '';
            loadProjects();
        } catch (err) { toast.error("Échec de la création du projet"); }
        finally { setUploading(false); }
    };

    const handleDelete = async (projectId, e) => {
        e.stopPropagation();
        if (!window.confirm("Supprimer ce projet ?")) return;
        try { await deleteProject(projectId); toast.success("Projet supprimé"); loadProjects(); }
        catch { toast.error("Échec de la suppression"); }
    };

    const handleDuplicate = async (projectId, e) => {
        e.stopPropagation();
        const tid = toast.loading('Duplication en cours…');
        try {
            const copy = await duplicateProject(projectId);
            setProjects((prev) => [copy, ...prev]);
            toast.dismiss(tid);
            toast.success('Projet dupliqué');
        } catch (err) {
            toast.dismiss(tid);
            toast.error(err?.response?.data?.detail || `Échec de la duplication (${err?.response?.status ?? 'réseau'})`);
        }
    };

    // Admin-only: flip a project's public flag. Optimistic local update so the
    // toggle reacts instantly; we revert on failure.
    const handleTogglePublic = async (project, e) => {
        e.stopPropagation();
        const next = !project.is_public;
        setProjects((prev) => prev.map((p) => p.id === project.id ? { ...p, is_public: next } : p));
        try {
            await setProjectPublic(project.id, next);
            toast.success(next ? 'Projet rendu public' : 'Projet remis en privé');
        } catch {
            setProjects((prev) => prev.map((p) => p.id === project.id ? { ...p, is_public: !next } : p));
            toast.error('Échec du changement');
        }
    };

    if (authLoading) return <div className="flex h-screen items-center justify-center bg-background"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>;

    if (!user) {
        return (
            <div className="min-h-screen bg-background flex flex-col">
                <header className="h-14 border-b border-border px-5 flex items-center justify-between bg-card">
                    <div className="flex items-center">
                        <img
                            src="/logo.svg"
                            alt="Kinote"
                            className="h-7 w-auto"
                            onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = '/logo.png'; }}
                        />
                    </div>
                    <div className="flex items-center gap-2">
                        <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => setShowLoginModal(true)} data-testid="header-login-btn">Se connecter</Button>
                        <ThemeToggle />
                    </div>
                </header>
                <div className="flex-1 flex flex-col items-center justify-center px-6 text-center">
                    <div className="max-w-md flex flex-col items-center space-y-6">
                        <img
                            src="/logo.svg"
                            alt="Kinote"
                            className="w-full max-w-[360px] h-auto"
                            onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = '/logo.png'; }}
                        />
                        <p className="text-muted-foreground text-base leading-relaxed">Importez votre vidéo. Obtenez un storyboard professionnel.<br />Exportez en PDF.</p>
                        <Button className="h-10 px-6 text-sm font-medium" onClick={() => setShowLoginModal(true)} data-testid="hero-get-started-btn">Commencer</Button>
                    </div>
                </div>
                <LoginModal open={showLoginModal} onClose={() => setShowLoginModal(false)} />
            </div>
        );
    }

    return (
        <Layout title="Tableau de bord" actions={
            <div className="flex items-center gap-3">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center overflow-hidden">
                        {user.picture ? <img src={user.picture} alt="" className="w-full h-full object-cover" /> : <User className="w-3 h-3 text-muted-foreground" />}
                    </div>
                    {editingUsername ? (
                        <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                            <input
                                autoFocus
                                className="h-6 text-xs rounded border border-primary bg-background px-1.5 focus:outline-none w-28"
                                value={usernameDraft}
                                onChange={(e) => setUsernameDraft(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') handleSaveUsername(); if (e.key === 'Escape') setEditingUsername(false); }}
                                onBlur={handleSaveUsername}
                            />
                            <button className="p-0.5 text-primary" onClick={handleSaveUsername}><Check className="w-3 h-3" /></button>
                            <button className="p-0.5 text-muted-foreground" onClick={() => setEditingUsername(false)}><X className="w-3 h-3" /></button>
                        </div>
                    ) : (
                        <button
                            className="group/un hidden sm:flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                            onClick={() => { setUsernameDraft(user.username || user.name || ''); setEditingUsername(true); }}
                            title="Modifier mon pseudo"
                            data-testid="user-display-name"
                        >
                            {user.username || user.name}
                            <Pencil className="w-2.5 h-2.5 opacity-0 group-hover/un:opacity-60" />
                        </button>
                    )}
                </div>
                <button onClick={logout} className="text-muted-foreground hover:text-foreground transition-colors" title="Se déconnecter" data-testid="logout-btn">
                    <LogOut className="w-4 h-4" />
                </button>
            </div>
        }>
            <div className="p-8 max-w-6xl mx-auto">
                {!showNewProject ? (
                    <div className="flex items-center gap-2 mb-8">
                        <Button variant="outline" className="h-9 text-sm gap-2" onClick={() => setShowNewProject(true)} data-testid="new-project-btn">
                            <Plus className="w-3.5 h-3.5" /> Nouveau projet
                        </Button>
                        <Button
                            variant="outline"
                            className="h-9 text-sm gap-2"
                            onClick={() => importInputRef.current?.click()}
                            disabled={importing}
                            title="Importer un projet depuis un ZIP Kinote"
                        >
                            {importing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FolderInput className="w-3.5 h-3.5" />}
                            Importer
                        </Button>
                        <input
                            ref={importInputRef}
                            type="file"
                            accept=".zip"
                            className="hidden"
                            onChange={handleImportZip}
                        />
                    </div>
                ) : (
                    <div className="mb-8 bg-card border border-border rounded-xl p-6 max-w-lg card-shadow" data-testid="new-project-form">
                        <h3 className="font-semibold text-sm mb-5">Nouveau projet</h3>
                        <form onSubmit={handleCreateProject} className="space-y-4">
                            <div>
                                <label className="text-xs text-muted-foreground mb-1.5 block font-medium">Titre</label>
                                <input type="text" className="w-full rounded-lg bg-background border border-input px-3 py-2 text-sm focus:ring-1 focus:ring-primary focus:border-primary outline-none transition-all duration-200" placeholder="Mon storyboard..." value={newTitle} onChange={e => setNewTitle(e.target.value)} required data-testid="project-title-input" />
                            </div>
                            <div>
                                <label className="text-xs text-muted-foreground mb-1.5 block font-medium">Format</label>
                                <div className="grid grid-cols-2 gap-2">
                                    {[
                                        { key: '16:9', label: '16:9', desc: 'Paysage (défaut)' },
                                        { key: '9:16', label: '9:16', desc: 'Portrait' },
                                    ].map(opt => (
                                        <button type="button" key={opt.key} onClick={() => setProjectFormat(opt.key)}
                                            className={`flex flex-col items-center gap-0.5 p-2.5 rounded-lg border text-center transition-all duration-200 cursor-pointer ${
                                                projectFormat === opt.key
                                                    ? 'border-primary bg-primary/5 text-foreground'
                                                    : 'border-border bg-background text-muted-foreground hover:border-primary/30'
                                            }`}>
                                            <span className="text-xs font-medium">{opt.label}</span>
                                            <span className="text-xs text-muted-foreground">{opt.desc}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div>
                                <label className="text-xs text-muted-foreground mb-1.5 block font-medium">Fichier vidéo <span className="opacity-50">(optionnel)</span></label>
                                <input ref={fileInputRef} type="file" accept=".mp4,.mov,.avi,.mkv,.webm,video/mp4,video/quicktime,video/x-msvideo,video/x-matroska,video/webm" className="w-full text-sm text-muted-foreground file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border file:border-border file:bg-card file:text-foreground file:cursor-pointer file:font-medium file:text-xs" data-testid="project-file-input" />
                                <p className="text-[11px] text-muted-foreground/60 mt-1">
                                    Formats acceptés : MP4, MOV, AVI, MKV, WEBM · Taille max : 500 MB · Durée max : 30 min
                                </p>
                            </div>
                            <div data-testid="sensitivity-selector">
                                <label className="text-xs text-muted-foreground mb-2 block font-medium">Détection des plans</label>
                                <div className="grid grid-cols-3 gap-2">
                                    {[
                                        { key: 'low', label: 'Faible', desc: 'Moins de coupes' },
                                        { key: 'normal', label: 'Standard', desc: 'Équilibré' },
                                        { key: 'high', label: 'Sensible', desc: 'Plus de coupes' },
                                    ].map(opt => (
                                        <button type="button" key={opt.key} onClick={() => setSensitivity(opt.key)}
                                            className={`flex flex-col items-center gap-0.5 p-2.5 rounded-lg border text-center transition-all duration-200 cursor-pointer ${
                                                sensitivity === opt.key
                                                    ? 'border-primary bg-primary/5 text-foreground'
                                                    : 'border-border bg-background text-muted-foreground hover:border-primary/30'
                                            }`} data-testid={`sensitivity-${opt.key}`}
                                        >
                                            <span className="text-xs font-medium">{opt.label}</span>
                                            <span className="text-xs text-muted-foreground">{opt.desc}</span>
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="flex gap-2 pt-1">
                                <Button type="submit" disabled={uploading} className="h-9 text-sm gap-2" data-testid="create-project-submit">
                                    {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />} Créer
                                </Button>
                                <Button variant="ghost" className="h-9 text-sm" onClick={() => setShowNewProject(false)} data-testid="cancel-new-project">Annuler</Button>
                            </div>
                        </form>
                    </div>
                )}

                {loading ? (
                    <div className="flex justify-center py-20"><Loader2 className="w-5 h-5 animate-spin text-muted-foreground" /></div>
                ) : projects.length === 0 ? (
                    <div className="text-center py-20 text-muted-foreground">
                        <div className="w-16 h-16 rounded-2xl border-2 border-dashed border-border mx-auto mb-4 flex items-center justify-center">
                            <Film className="w-6 h-6 opacity-30" />
                        </div>
                        <p className="text-sm font-medium">Aucun projet pour l'instant</p>
                        <p className="text-xs mt-1 text-muted-foreground/60">Créez votre premier storyboard</p>
                    </div>
                ) : (() => {
                    // Public templates = explicitly public AND confirmed not owned by this
                    // user (is_owner === false). Everything else goes to personal —
                    // this is deliberately defensive: if is_owner is null/undefined
                    // (e.g. old projects without the field), the project stays visible.
                    const publicProjects = projects.filter((p) => p.is_public === true && p.is_owner === false);
                    const personal = projects.filter((p) => !(p.is_public === true && p.is_owner === false));
                    const isAdmin = !!user?.is_admin;

                    const renderCard = (project) => {
                        const ownedByMe = project.is_owner;
                        return (
                            <div key={project.id}
                                className="group bg-card border border-border rounded-xl overflow-hidden cursor-pointer card-shadow card-lift relative"
                                onClick={() => { console.log('open project', project.id, project.is_owner, project.is_public); navigate(`/project/${project.id}`); }}
                                data-testid={`project-card-${project.id}`}
                            >
                                <div className="aspect-video bg-secondary/30 relative overflow-hidden rounded-t-xl">
                                    {project.shots?.[0]?.frame_path ? (
                                        <img src={(process.env.REACT_APP_BACKEND_URL || '') + project.shots[0].frame_path} alt="" className={`w-full h-full ${project.format === '9:16' ? 'object-contain' : 'object-cover'}`} onError={(e) => e.target.style.display = 'none'} />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center">
                                            <Film className="w-8 h-8 text-muted-foreground/15" />
                                        </div>
                                    )}
                                    {project.status === 'processing' && (
                                        <div className="absolute inset-0 bg-background/80 flex items-center justify-center backdrop-blur-sm">
                                            <Loader2 className="w-5 h-5 animate-spin text-primary" />
                                        </div>
                                    )}
                                    {project.is_public && (
                                        <span className="absolute top-2 left-2 inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-primary/90 text-primary-foreground">
                                            <Globe className="w-2.5 h-2.5" /> Public
                                        </span>
                                    )}
                                </div>
                                <div className="p-4">
                                    <div className="flex justify-between items-start">
                                        <div className="min-w-0 flex-1">
                                            <h3 className="font-medium text-sm truncate">{project.title}</h3>
                                            <p className="text-xs text-muted-foreground mt-0.5">
                                                {project.shots?.length || 0} plans
                                                {project.shots?.length > 0 && <span className="ml-1.5 opacity-60">{formatDuration(project.shots.reduce((a, s) => a + (s.duration || 0), 0))}</span>}
                                            </p>
                                        </div>
                                        <div className="flex items-center gap-0.5 shrink-0">
                                            {isAdmin && ownedByMe && (
                                                <button
                                                    className={`p-1.5 -m-1 transition-all ${project.is_public ? 'text-primary opacity-100' : 'text-muted-foreground opacity-40 group-hover:opacity-100'} hover:text-primary`}
                                                    onClick={(e) => handleTogglePublic(project, e)}
                                                    title={project.is_public ? 'Rendre privé' : 'Rendre public'}
                                                    data-testid={`toggle-public-${project.id}`}
                                                >
                                                    {project.is_public ? <Globe className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                                                </button>
                                            )}
                                            <button
                                                className="text-muted-foreground opacity-40 group-hover:opacity-100 hover:text-foreground transition-all p-1.5 -m-1"
                                                onClick={(e) => handleDuplicate(project.id, e)}
                                                title="Dupliquer ce projet"
                                                data-testid={`duplicate-project-${project.id}`}
                                            >
                                                <Copy className="w-4 h-4" />
                                            </button>
                                            {ownedByMe && (
                                                <button className="text-muted-foreground opacity-40 group-hover:opacity-100 hover:text-destructive transition-all p-1.5 -m-1"
                                                    onClick={(e) => handleDelete(project.id, e)} data-testid={`delete-project-${project.id}`}>
                                                    <Trash2 className="w-4 h-4" />
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    };

                    return (
                        <div className="space-y-10" data-testid="projects-grid">
                            {personal.length > 0 && (
                                <div className="space-y-3">
                                    <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Mes projets</h2>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                                        {personal.map(renderCard)}
                                    </div>
                                </div>
                            )}
                            {publicProjects.length > 0 && (
                                <div className="space-y-3">
                                    <div className="flex items-center gap-2">
                                        <h2 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Modèles publics</h2>
                                        <Globe className="w-3 h-3 text-muted-foreground/60" />
                                    </div>
                                    <p className="text-xs text-muted-foreground/70">Projets de référence partagés par l'administrateur (lecture seule).</p>
                                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                                        {publicProjects.map(renderCard)}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })()}
            </div>
        </Layout>
    );
}
